import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Typography,
    Grid,
    Box,
    Chip,
    Divider,
    Paper,
    Avatar
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import GavelIcon from '@mui/icons-material/Gavel';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Helper for Priority Chips
const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
        case 'high': return 'error';
        case 'medium': return 'warning';
        case 'low': return 'info';
        default: return 'default';
    }
};

const CaseDetailsDialog = ({ open, handleClose, caseData }) => {
    if (!caseData) return null;

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { 
                    borderRadius: 3, 
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)' 
                }
            }}
        >
            {/* Header Section */}
            <DialogTitle sx={{ m: 0, p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="h6" component="h2" sx={{ fontWeight: 700, color: 'primary.main', leading: 1.2 }}>
                            Case No: {caseData.case_no || 'N/A'}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                            Type: <strong>{caseData.case_type_name || "N/A"}</strong>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Title: <strong>{caseData.case_title || "N/A"}</strong>
                        </Typography>
                    </Box>
                </Box>

                <IconButton
                    onClick={handleClose}
                    aria-label="close"
                    size="small"
                    sx={{ color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Divider />

            {/* Dialog Body */}
            <DialogContent sx={{ p: 3 }}>
                <Grid container spacing={2.5}>

                    {/* Section 1: Case Overview */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', fontWeight: 700, mb: 1.5, letterSpacing: 0.5 }}>
                            CASE OVERVIEW
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={6} sm={3}>
                                    <Typography variant="caption" color="text.secondary" display="block">FIR NO</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{caseData.fir_no || 'N/A'}</Typography>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Typography variant="caption" color="text.secondary" display="block">Filing Date</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDate(caseData.filing_date)}</Typography>
                                </Grid>
                                <Grid item xs={4} sm={2}>
                                    <Typography variant="caption" color="text.secondary" display="block">Priority</Typography>
                                    <Chip 
                                        label={caseData.priority || 'N/A'} 
                                        size="small" 
                                        color={getPriorityColor(caseData.priority)} 
                                        sx={{ mt: 0.5, fontWeight: 600, textTransform: 'capitalize' }} 
                                    />
                                </Grid>
                                <Grid item xs={4} sm={2}>
                                    <Typography variant="caption" color="text.secondary" display="block">Status</Typography>
                                    <Chip 
                                        label={caseData.status || 'N/A'} 
                                        size="small" 
                                        variant="outlined" 
                                        color="primary" 
                                        sx={{ mt: 0.5, fontWeight: 600 }} 
                                    />
                                </Grid>
                                <Grid item xs={4} sm={2}>
                                    <Typography variant="caption" color="text.secondary" display="block">Stage</Typography>
                                    <Chip 
                                        label={caseData.stage_name || 'N/A'} 
                                        size="small" 
                                        variant="outlined" 
                                        color="secondary" 
                                        sx={{ mt: 0.5, fontWeight: 600 }} 
                                    />
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>

                    {/* Section 2: Court & Opponent Details */}
                    <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', fontWeight: 700, mb: 1.5, letterSpacing: 0.5 }}>
                            COURT DETAILS
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2, height: '100%' }}>
                            <Box sx={{ mb: 1.5 }}>
                                <Typography variant="caption" color="text.secondary" display="block">Court Name</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{caseData.court_name || 'N/A'}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary" display="block">Judge Name</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{caseData.judge_name || 'N/A'}</Typography>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', fontWeight: 700, mb: 1.5, letterSpacing: 0.5 }}>
                            OPPOSITE PARTY
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2, height: '100%' }}>
                            <Typography variant="caption" color="text.secondary" display="block">Opponent Name</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
                                {caseData.opposite_party || 'N/A'}
                            </Typography>
                        </Paper>
                    </Grid> */}

                    {/* Section 3: Client Details */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', fontWeight: 700, mb: 1.5, letterSpacing: 0.5 }}>
                            CLIENT(S) INFORMATION
                        </Typography>
                        {caseData.client_names && caseData.client_names.length > 0 ? (
                            <Grid container spacing={1.5}>
                                {caseData.client_names.map((name, index) => (
                                    <Grid item xs={12} sm={4} key={index}>
                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                p: 1.5,
                                                bgcolor: 'background.paper',
                                                borderRadius: 2,
                                                borderStyle: 'dashed',
                                                borderColor: 'divider',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.5
                                            }}
                                        >
                                            <Avatar sx={{ bgcolor: 'primary.light', width: 32, height: 32, fontSize: 14 }}>
                                                {name ? name.charAt(0).toUpperCase() : <PersonIcon />}
                                            </Avatar>
                                            <Box sx={{ overflow: 'hidden' }}>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                                    {name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-all' }} noWrap>
                                                    {caseData.client_emails?.[index] || 'No Email Registered'}
                                                </Typography>
                                            </Box>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ fst: 'italic' }}>
                                No client information available.
                            </Typography>
                        )}
                    </Grid>

                    {/* Section 4: Description & Timestamps */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ color: 'primary.main', fontWeight: 700, mb: 1.5, letterSpacing: 0.5 }}>
                            ADDITIONAL INFO
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Description</Typography>
                                <Typography variant="body2" color={caseData.description ? 'text.primary' : 'text.secondary'} sx={{ lineHeight: 1.6 }}>
                                    {caseData.description || 'No description provided.'}
                                </Typography>
                            </Box>
                            <Divider sx={{ my: 1.5 }} />
                            <Grid container spacing={2}>
                                {/* <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="text.secondary">Created At: </Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 600, ml: 0.5 }}>{formatDate(caseData.created_at)}</Typography>
                                </Grid> */}
                                {/* <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="text.secondary">Last Updated: </Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 600, ml: 0.5 }}>{formatDate(caseData.updated_at)}</Typography>
                                </Grid> */}
                            </Grid>
                        </Paper>
                    </Grid>

                </Grid>
            </DialogContent>

            <Divider />

            {/* Dialog Footer Actions */}
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button 
                    onClick={handleClose} 
                    variant="contained" 
                    color="primary" 
                    disableElevation
                    sx={{ px: 4, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CaseDetailsDialog;